import { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { LoginPage } from "@components/LoginPage";
import { Dashboard } from "@pages/Dashboard";
import { Artists } from "@pages/Artists";
import { Users } from "@pages/Users";
import { Tabs } from "@pages/Tabs";
import { TabSubmissions } from "@pages/TabSubmissions";
import { Layout } from "@components/Layout";
import { useCheckIsAdmin } from "@api/auth";

export function Login() {
  const [token, setToken] = useState<string | null>(null);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(false);
  const [adminError, setAdminError] = useState<string | null>(null);
  const checkAdminMutation = useCheckIsAdmin();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const storedToken = localStorage.getItem("admin_token");
    if (storedToken) {
      // Verify admin status on mount
      verifyAdminAccess(storedToken);
    }
  }, []);

  const verifyAdminAccess = async (authToken: string) => {
    setIsCheckingAdmin(true);
    setAdminError(null);
    
    // Temporarily set token to check admin status
    localStorage.setItem("admin_token", authToken);
    
    try {
      const result = await checkAdminMutation.mutateAsync();
      if (!result.is_admin) {
        localStorage.removeItem("admin_token");
        setAdminError("Access denied. Admin credentials required.");
        return false;
      } else {
        setToken(authToken);
        // Navigate to dashboard or the current path if it's a valid route
        const currentPath = location.pathname;
        if (currentPath === "/" || currentPath === "/login") {
          navigate("/dashboard", { replace: true });
        } else {
          navigate(currentPath, { replace: true });
        }
        return true;
      }
    } catch (error) {
      localStorage.removeItem("admin_token");
      setAdminError(error instanceof Error ? error.message : "Failed to verify admin access");
      return false;
    } finally {
      setIsCheckingAdmin(false);
    }
  };

  const handleLogin = async (newToken: string) => {
    await verifyAdminAccess(newToken);
  };

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem("admin_token");
    navigate("/login", { replace: true });
  };


  // Get current page from URL path
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === "/dashboard" || path === "/") return "dashboard";
    if (path === "/artists") return "artists";
    if (path === "/users") return "users";
    if (path === "/tabs") return "tabs";
    if (path === "/tab-submissions") return "tab-submissions";
    return "dashboard";
  };

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <>
            {isCheckingAdmin && (
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <p className="text-muted-foreground">Verifying admin access...</p>
                </div>
              </div>
            )}
            {!isCheckingAdmin && (
              <LoginPage 
                onLogin={handleLogin}
                error={adminError || undefined}
              />
            )}
          </>
        }
      />
      {token ? (
        <Route
          path="/*"
          element={<Layout currentPage={getCurrentPage()} onLogout={handleLogout} />}
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="artists" element={<Artists />} />
          <Route path="users" element={<Users />} />
          <Route path="tabs" element={<Tabs />} />
          <Route path="tab-submissions" element={<TabSubmissions />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      ) : (
        <Route path="*" element={<Navigate to="/login" replace />} />
      )}
    </Routes>
  );
}

